import { useHasInvoicing } from '~/hooks/useFeatureFlag';
import { UserRole } from '~/types/company';
import {
  Document,
  DocumentDirection,
  DocumentSource,
  DocumentStatus,
  DocumentType,
  EInvoiceType,
} from '~/types/document';
import { DocumentSectional } from '~/types/document-sectional';

export const getIsInflow = (documentType: DocumentType, direction: DocumentDirection): boolean => {
  if (
    documentType === DocumentType.Bill ||
    documentType === DocumentType.DeliveryNote ||
    documentType === DocumentType.Quote
  ) {
    return true;
  }
  if (documentType === DocumentType.Receipt || documentType === DocumentType.Payroll) {
    return false;
  }
  if (direction === DocumentDirection.issued) {
    switch (documentType) {
      case DocumentType.Invoice:
      case DocumentType.DebitNote:
      case DocumentType.Parcel:
      case DocumentType.SelfInvoice:
      case DocumentType.Other:
        return true;
      case DocumentType.CreditNote:
        return false;
    }
  } else {
    switch (documentType) {
      case DocumentType.Invoice:
      case DocumentType.DebitNote:
      case DocumentType.Parcel:
      case DocumentType.SelfInvoice:
      case DocumentType.Other:
        return false;
      case DocumentType.CreditNote:
        return true;
    }
  }
};

export const useCanEditDocument = () => {
  const hasInvoicing = useHasInvoicing([UserRole.ENTREPRENEUR]);
  if (hasInvoicing) {
    return (document: Document): boolean => document.editable;
  }

  return (document: Document): boolean => {
    if (!document.editable) return false;

    return !document.isEInvoice;
  };
};

export const useCanDeleteDocument = () => {
  const hasInvoicing = useHasInvoicing();

  if (hasInvoicing) {
    return (document: Document): boolean => document.deletable;
  }

  return (document: Document): boolean => {
    if (!document.deletable) return false;

    return !document.isEInvoice;
  };
};

export const useCanDuplicateDocument = () => {
  const hasInvoicing = useHasInvoicing([UserRole.ENTREPRENEUR]);

  if (hasInvoicing) {
    return (): boolean => true;
  }

  return (): boolean => false;
};

export const useShouldEditInBuilder = () => {
  const hasInvoicing = useHasInvoicing();
  return (document: Document): boolean => {
    if (!hasInvoicing) {
      return false;
    }
    if (document.documentType === DocumentType.Bill) {
      return false;
    }
    if (document.documentType === DocumentType.Other) {
      return false;
    }
    if (
      [DocumentStatus.Draft, DocumentStatus.Discarded, DocumentStatus.Refused].includes(document.status) &&
      document.direction === DocumentDirection.received &&
      document.documentType === DocumentType.Invoice
    ) {
      return true;
    }
    return document.direction === DocumentDirection.issued || document.documentType === DocumentType.SelfInvoice;
  };
};

export const canPreviewDocument = (document: Document): boolean => {
  if (document.documentType === DocumentType.Bill) return false;

  if ([DocumentStatus.Draft, DocumentStatus.Discarded].includes(document.status)) {
    return false;
  }
  if (!document.isEInvoice || !document.hasAttachment) {
    return false;
  }
  return true;
};

export const isCostCenterAllocableDocument = (document: Document): boolean =>
  document.documentType !== DocumentType.DeliveryNote && document.status !== DocumentStatus.Draft;

export const documentPreviewType = <T extends { source: Document['source'] }>(val: T) => {
  if (val.source === DocumentSource.User) return 'sibill';
  return 'xml';
};

export const isSelfInvoiceTD26 = (
  type: DocumentType | null | undefined,
  subtype: EInvoiceType | null | undefined,
): boolean => {
  return subtype === EInvoiceType.AutofatturaCessioneDiBeniAmmortizzabili && type === DocumentType.SelfInvoice;
};

export const buildNumber = (
  sectional: Partial<DocumentSectional> | null | undefined,
  progressive: number | bigint | null | undefined,
): string => {
  const padded = progressive ? String(progressive).padStart(sectional?.minimumDigits ?? 1, '0') : '';
  const prefix = sectional?.prefix ?? '';
  const suffix = sectional?.suffix ?? '';
  return `${prefix}${padded}${suffix}`;
};
